import { getGameConfig } from '../../../config/gameConfig';
import { createHouses } from '../factory/houseFactory';
import { getRandomPersonName } from '../utils/peopleNameGenerator';
import { hashStringSeed, mulberry32 } from '../utils/random';
import { getHouseCapacity } from '../utils/house';
import { getMaxWarmth } from '../utils/warmth';
import type { House, Settlement } from '../types';

export function syncHouseAssignments(settlement: Settlement): void {
    const houseMap = new Map<string, House>();
    settlement.houses.forEach((house) => {
        house.people = [];
        if (!house.storage || typeof house.storage !== 'object') {
            house.storage = { food: 0, wood: 0, charcoal: 0 };
        }
        if (!Number.isFinite(house.storage.food)) house.storage.food = 0;
        if (!Number.isFinite(house.storage.wood)) house.storage.wood = 0;
        if (!Number.isFinite(house.storage.charcoal)) house.storage.charcoal = 0;
        const maxWarmth = getGameConfig().heat.maxWarmth[house.type];
        if (!Number.isFinite(house.warmth)) house.warmth = maxWarmth;
        house.warmth = Math.max(0, Math.min(maxWarmth, house.warmth));
        houseMap.set(house.id, house);
    });

    const maxWarmth = getMaxWarmth();
    settlement.people.forEach((person) => {
        if (!person.name || person.name.trim().length === 0) {
            const nameSeed = hashStringSeed(person.id);
            person.name = getRandomPersonName(getGameConfig().ui.language, mulberry32(nameSeed));
        }
        if (!Number.isFinite(person.hunger) || person.hunger < 0) person.hunger = 0;
        if (!Number.isFinite(person.warmth)) person.warmth = maxWarmth;
        if (person.warmth < 0) person.warmth = 0;
        if (!person.houseId || !houseMap.has(person.houseId)) {
            person.houseId = null;
            return;
        }
        houseMap.get(person.houseId)?.people.push(person.id);
    });
}

export function ensurePeopleHoused(settlement: Settlement): void {
    const capacity = getHouseCapacity();
    const unassigned = settlement.people.filter((person) => !person.houseId);
    if (unassigned.length === 0) return;
    const freeSlots = settlement.houses.map((house) => capacity - house.people.length);
    let houseIndex = 0;
    for (const person of unassigned) {
        while (houseIndex < freeSlots.length && freeSlots[houseIndex] <= 0) {
            houseIndex += 1;
        }
        if (houseIndex >= settlement.houses.length) break;
        const house = settlement.houses[houseIndex];
        person.houseId = house.id;
        house.people.push(person.id);
        freeSlots[houseIndex] -= 1;
    }
}

export function ensureHouses(settlement: Settlement): void {
    const woodCount = Math.max(0, settlement.buildings.houses);
    const stoneCount = Math.max(0, settlement.buildings.stoneHouse);
    if (!settlement.houses) {
        settlement.houses = [
            ...createHouses(settlement, woodCount, 'wooden'),
            ...createHouses(settlement, stoneCount, 'stone'),
        ];
    }

    const woodHouses = settlement.houses.filter((house) => house.type === 'wooden');
    const stoneHouses = settlement.houses.filter((house) => house.type === 'stone');
    const removedIds = new Set<string>();

    if (woodHouses.length > woodCount) {
        const removed = woodHouses.splice(woodCount);
        removed.forEach((house) => removedIds.add(house.id));
    } else if (woodHouses.length < woodCount) {
        const add = woodCount - woodHouses.length;
        woodHouses.push(...createHouses(settlement, add, 'wooden'));
    }

    if (stoneHouses.length > stoneCount) {
        const removed = stoneHouses.splice(stoneCount);
        removed.forEach((house) => removedIds.add(house.id));
    } else if (stoneHouses.length < stoneCount) {
        const add = stoneCount - stoneHouses.length;
        stoneHouses.push(...createHouses(settlement, add, 'stone'));
    }

    settlement.houses = [...woodHouses, ...stoneHouses];
    if (removedIds.size > 0) {
        settlement.people.forEach((person) => {
            if (person.houseId && removedIds.has(person.houseId)) {
                person.houseId = null;
            }
        });
    }
    syncHouseAssignments(settlement);
    ensurePeopleHoused(settlement);
}
